import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Button } from "@/components/ui/button";

import * as XLSX from "xlsx";
import { toast } from "sonner";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { RotateCcw, Download, Copy } from "lucide-react";

import { FIELD_RENDERERS } from "./fieldRenderers";

import SummaryCards from "./SummaryCards";
import ChartRenderer from "./ChartRenderer";
import DataTable from "./DataTable";

import { ACTIONS, FIELD_TYPES } from "@/utils/calculatorSchema";

/* -------------------------------------------------------------------------- */
/*                          DEFAULT INITIAL VALUES                            */
/* -------------------------------------------------------------------------- */

function buildInitialValues(fields) {
  return fields.reduce((acc, field) => {
    if (!field.key) return acc;

    acc[field.key] = field.defaultValue ?? field.value ?? "";

    return acc;
  }, {});
}

/* -------------------------------------------------------------------------- */
/*                           VALIDATION ENGINE                                */
/* -------------------------------------------------------------------------- */

function sanitizeValue(field, value) {
  if (value === "" || value === null || value === undefined) {
    return value;
  }

  // Select fields store string values.
  if (field.type === FIELD_TYPES.SELECT || field.type === FIELD_TYPES.ARRAY) {
    return value;
  }

  let next = Number(value);

  if (Number.isNaN(next)) {
    return field.defaultValue ?? "";
  }

  if (field.min !== undefined) {
    next = Math.max(next, field.min);
  }

  if (field.max !== undefined) {
    next = Math.min(next, field.max);
  }

  return next;
}

/* -------------------------------------------------------------------------- */
/*                          GENERIC CALCULATOR                                */
/* -------------------------------------------------------------------------- */

export default function LoanCalculator({ config }) {
  const {
    title,
    subtitle,

    fields,

    calculate,

    charts = [],

    tables = [],

    actions = [],
  } = config;

  const [values, setValues] = useState(() => buildInitialValues(fields));

  const [activeTab, setActiveTab] = useState(() => tables[0]?.dataKey ?? "");

  /* ---------------------------------------------------------------------- */
  /*                          FIELD UPDATE                                  */
  /* ---------------------------------------------------------------------- */

  const updateField = useCallback(
    (key, value) => {
      const field = fields.find((f) => f.key === key);

      if (!field) return;

      setValues((prev) => ({
        ...prev,
        [key]: sanitizeValue(field, value),
      }));
    },
    [fields],
  );

  /* ---------------------------------------------------------------------- */
  /*                          RESET                                          */
  /* ---------------------------------------------------------------------- */

  const reset = useCallback(() => {
    setValues(buildInitialValues(fields));
  }, [fields]);

  /* ---------------------------------------------------------------------- */
  /*                     MAIN CALCULATION ENGINE                             */
  /* ---------------------------------------------------------------------- */
  const results = useMemo(() => {
    return calculate(values);
  }, [values, calculate]);

  /* ---------------------------------------------------------------------- */
  /*               RESET HIDDEN FIELD VALUES                                 */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    setValues((previous) => {
      let changed = false;

      const next = { ...previous };

      fields.forEach((field) => {
        if (
          typeof field.visible === "function" &&
          !field.visible({
            values: previous,
          })
        ) {
          const defaultValue = field.defaultValue ?? "";

          if (next[field.key] !== defaultValue) {
            next[field.key] = defaultValue;
            changed = true;
          }
        }
      });

      return changed ? next : previous;
    });
  }, [fields, values]);

  /* ---------------------------------------------------------------------- */
  /*                     TABLE GENERATORS                                    */
  /* ---------------------------------------------------------------------- */

  const generatedTables = tables;

  /* ---------------------------------------------------------------------- */
  /*                              ACTIONS                                   */
  /* ---------------------------------------------------------------------- */

  const copyResults = useCallback(async () => {
    const text = JSON.stringify(results, null, 2);

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);

        toast.success("Results copied to clipboard.");

        return;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";

      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const success = document.execCommand("copy");

      textarea.remove();

      if (success) {
        toast.success("Results copied to clipboard.");
      } else {
        toast.error("Failed to copy results.");
      }
    } catch (error) {
      console.error(error);

      toast.error("Failed to copy results.");
    }
  }, [results]);

  function buildInputSheet(fields, values) {
    const rows = [];

    fields.forEach((field) => {
      if (!field.key) return;

      // Ignore headings/visual elements
      if (
        field.type === FIELD_TYPES.SECTION ||
        field.type === FIELD_TYPES.BUTTON ||
        field.type === FIELD_TYPES.HTML
      ) {
        return;
      }

      // Ignore hidden fields
      if (typeof field.visible === "function" && !field.visible({ values })) {
        return;
      }

      let value = values[field.key];

      if (value === undefined || value === null) {
        value = "";
      }

      // Array fields
      if (Array.isArray(value)) {
        value = value
          .map((item, index) => {
            if (typeof item !== "object") {
              return item;
            }

            return Object.entries(item)
              .map(([key, val]) => `${key}: ${val} `)
              .join(" | ");
          })
          .join("\n");
      }

      rows.push({
        Field: field.label,
        Value: value,
      });
    });

    return rows;
  }

  const downloadExcel = useCallback(() => {
    try {
      const workbook = XLSX.utils.book_new();

      /* -------------------------------------------------- */
      /* Summary Sheet                                      */
      /* -------------------------------------------------- */

      const summaryRows = (config.summaryCards ?? []).map((card) => {
        let value = results.summary?.[card.key];

        if (value && typeof value === "object" && card.displayKey) {
          value = value[card.displayKey];
        }

        // Fallback: some cards use displayKey as the actual summary key
        if (
          (value === undefined || value === null || value === "") &&
          card.displayKey
        ) {
          value = results.summary?.[card.displayKey];
        }

        return {
          Metric: card.title,
          Value: value ?? "",
        };
      });

      const summarySheet = XLSX.utils.json_to_sheet(summaryRows);

      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      /* -------------------------------------------------- */
      /* Calculator Specific Sheets                         */
      /* -------------------------------------------------- */

      /* -------------------------------------------------- */
      /* Inputs Sheet                                       */
      /* -------------------------------------------------- */

      const inputRows = buildInputSheet(config.fields, values);

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(inputRows),
        "Inputs",
      );

      /* -------------------------------------------------- */
      /* Additional Sheets                                 */
      /* -------------------------------------------------- */

      const sheets =
        config.exportExcel?.({
          values,
          results,
        }) ?? {};

      Object.entries(sheets).forEach(([sheetName, rows]) => {
        if (!Array.isArray(rows) || rows.length === 0) {
          return;
        }

        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(rows),
          sheetName.substring(0, 31),
        );
      });

      XLSX.writeFile(workbook, `${config.id}.xlsx`);

      toast.success("Excel downloaded successfully.");
    } catch (error) {
      console.error(error);

      toast.error("Failed to generate Excel.");
    }
  }, [config, values, results]);

  /* ---------------------------------------------------------------------- */
  /*                          FIELD RENDERING                               */
  /* ---------------------------------------------------------------------- */

  const renderedFields = useMemo(() => {
    return fields
      .filter((field) => {
        if (typeof field.visible !== "function") {
          return true;
        }

        return field.visible({
          values,
        });
      })
      .map((field) => {
        if (field.type === FIELD_TYPES.SECTION) {
          return (
            <div key={field.title} className="col-span-full mt-8 mb-2">
              <h2 className="text-xl font-semibold">{field.title}</h2>

              {field.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {field.description}
                </p>
              )}
            </div>
          );
        }
        const Component = FIELD_RENDERERS[field.type];

        if (!Component) return null;

        const {
          key: fieldKey,
          type,
          label,
          description,
          required,
          ...inputProps
        } = field;

        return (
          <motion.div
            key={field.key}
            layout
            initial={{
              opacity: 0,
              y: 10,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.2,
            }}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <label htmlFor={field.key} className="text-sm font-medium">
                {field.label}

                {field.required && (
                  <span className="ml-1 text-destructive">*</span>
                )}
              </label>
            </div>

            <Component
              key={fieldKey}
              id={fieldKey}
              field={field}
              value={values[fieldKey]}
              onChange={(value) => updateField(fieldKey, value)}
              {...inputProps}
            />

            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
            {field.helperText && (
              <p className="text-xs text-muted-foreground">
                {field.helperText}
              </p>
            )}
          </motion.div>
        );
      });
  }, [fields, values, updateField]);

  /* ---------------------------------------------------------------------- */
  /*                                 UI                                     */
  /* ---------------------------------------------------------------------- */

  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 24,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: 0.35,
      }}
      className="space-y-8"
    >
      <Card>
        <CardHeader className="space-y-2">
          <CardTitle className="text-3xl">{title}</CardTitle>

          {subtitle && <CardDescription>{subtitle}</CardDescription>}
        </CardHeader>

        <CardContent className="space-y-8">
          {/* -------------------------------------------------- */}
          {/* Toolbar                                             */}
          {/* -------------------------------------------------- */}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {actions.includes(ACTIONS.RESET) && (
              <Button variant="outline" onClick={reset}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            )}

            {actions.includes(ACTIONS.COPY) && (
              <Button variant="outline" onClick={copyResults}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            )}

            {actions.includes(ACTIONS.EXCEL) && (
              <Button variant="outline" onClick={downloadExcel}>
                <Download className="mr-2 h-4 w-4" />
                Excel
              </Button>
            )}

            {actions.includes(ACTIONS.CSV) && (
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                CSV
              </Button>
            )}

            {actions.includes(ACTIONS.PDF) && (
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                PDF
              </Button>
            )}
          </div>

          {/* -------------------------------------------------- */}
          {/* Dynamic Fields                                     */}
          {/* -------------------------------------------------- */}

          <div
            className={`
                            grid
                            gap-6

                            ${fields.length === 1 ? "grid-cols-1" : ""}

                            ${
                              fields.length === 2
                                ? "grid-cols-1 md:grid-cols-2"
                                : ""
                            }

                            ${
                              fields.length >= 3
                                ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                                : ""
                            }
                        `}
          >
            {renderedFields}
          </div>

          {/* -------------------------------------------------- */}
          {/* Summary                                             */}
          {/* -------------------------------------------------- */}

          <AnimatePresence mode="wait">
            <motion.div
              layout
              initial={{
                opacity: 0,
                y: 12,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                y: -12,
              }}
              transition={{
                duration: 0.25,
              }}
            >
              <SummaryCards results={results} config={config.summaryCards} />
            </motion.div>
          </AnimatePresence>

          {/* -------------------------------------------------- */}
          {/* Charts                                              */}
          {/* -------------------------------------------------- */}

          {charts.length > 0 && (
            <div className="grid gap-6 xl:grid-cols-2">
              {charts
                .filter((chart) =>
                  chart.visible ? chart.visible({ values, results }) : true,
                )
                .map((chart) => (
                  <ChartRenderer
                    key={chart.id}
                    chart={chart}
                    values={values}
                    results={results}
                    chartData={results.charts}
                  />
                ))}
            </div>
          )}

          {/* -------------------------------------------------- */}
          {/* Extra Insights                                     */}
          {/* -------------------------------------------------- */}

          {config.renderInsights?.({
            values,
            results,
          })}

          {/* -------------------------------------------------- */}
          {/* Tabs                                                */}
          {/* -------------------------------------------------- */}

          {generatedTables.length > 0 && (
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="space-y-6"
            >
              <TabsList className="flex flex-wrap">
                {generatedTables.map((table) => (
                  <TabsTrigger key={table.dataKey} value={table.dataKey}>
                    {table.title ?? table.type}
                  </TabsTrigger>
                ))}
              </TabsList>

              {generatedTables.map((table) => {
                const TableComponent = table.component ?? DataTable;

                return (
                  <TabsContent key={table.dataKey} value={table.dataKey}>
                    <TableComponent
                      columns={table.columns}
                      data={results.tables?.[table.dataKey] ?? []}
                      values={values}
                      results={results}
                      {...table.props}
                    />
                  </TabsContent>
                );
              })}
            </Tabs>
          )}

          {/* -------------------------------------------------- */}
          {/* Footer                                              */}
          {/* -------------------------------------------------- */}

          {config.footer && (
            <div className="border-t pt-6">
              {config.footer({
                values,
                results,
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
